import React from 'react'
import ReactDOM from 'react-dom/client'
import { captureInstallPrompt } from './utils/install'
import { registerSW } from 'virtual:pwa-register'
import App from './App.tsx'
import './styles/globals.css'

// Applied before first paint so the theme doesn't flash on load.
// Light is the default; dark is opt-in from the menu.
if (localStorage.getItem('kermes.theme') === 'dark') {
  document.documentElement.classList.add('dark')
}

/**
 * Telefon klavyesinin kapladığı yükseklik, CSS'e --kb olarak.
 *
 * iPhone klavye açılınca sayfayı küçültmüyor, üstüne çıkıyor: ekranın altına
 * sabitlenmiş sipariş bölümü (ve içindeki bahşiş tutarı) klavyenin arkasında
 * kalıyordu. Görünür alanın boyundan klavyeyi hesaplayıp bölümü onun üstüne
 * kaldırıyoruz (SalePage.css .cart).
 */
if (window.visualViewport) {
  const vv = window.visualViewport
  const sync = () => {
    const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
    document.documentElement.style.setProperty('--kb', `${Math.round(kb)}px`)
    document.documentElement.style.setProperty('--vvh', `${Math.round(vv.height)}px`)
  }
  vv.addEventListener('resize', sync)
  vv.addEventListener('scroll', sync)
  sync()
}

/**
 * Set by the till screen while a sale is half rung up. An update that reloads
 * the page mid-order would throw the cart away, so the swap waits, but only
 * for that, never for a plain idle screen. Asking the operator to confirm
 * instead just left people stranded on a stale build.
 */
declare global {
  interface Window {
    __adissooCartBusy?: boolean
    /** Reload onto the newest build; see below. */
    __adissooRefresh?: (minWaitMs?: number) => Promise<void>
  }
}

/** How often a long-lived window asks whether a new build has been deployed. */
const UPDATE_CHECK_MS = 60_000

const updateSW = registerSW({
  /**
   * Checks for a new build periodically, not only at startup.
   *
   * The till runs in a kiosk window that stays open for the whole service, and
   * registerSW otherwise looks exactly once, when the page first loaded. That
   * machine would sit on the build it started with for days while every phone
   * that reopened the app picked up fixes, which is precisely what happened.
   *
   * Closing the kiosk window does not help either: Chrome keeps running in the
   * background and the worker survives with it.
   */
  onRegisteredSW(_url, registration) {
    if (!registration) return
    setInterval(() => {
      // Pointless offline, and it logs a failed fetch every minute.
      if (!navigator.onLine) return
      void registration.update()
    }, UPDATE_CHECK_MS)
  },

  /**
   * Yeni sürüm indi ve bekliyor (20 Eki 2026). Hemen geçilmiyor: açılışta
   * ya da kullanım sırasında sayfanın kapanıp yeniden açılması titreme gibi
   * görünüyordu. Sessiz bir anda geçiliyor: uygulama arka plana alınınca ya
   * da bir dakika dokunulmamışken, sepet boşsa. Aşağı çekip yenileme beklemez.
   */
  onNeedRefresh() {
    updateReady = true
    // Müşterinin QR menüsü (30 Eki 2026): beklemeden yeni sürüm. Masadaki
    // müşteri sürekli dokunuyor, bir dakikalık sessiz an hiç gelmiyordu ve
    // tablet eski menüde (masadan siparişsiz) kalıyordu. Sepet sessionStorage'da,
    // yenilemede kaybolmuyor.
    if (window.location.pathname.startsWith('/m/') || window.location.pathname === '/m') {
      applyUpdate()
      return
    }
    const timer = window.setInterval(() => {
      if (!updateReady) return window.clearInterval(timer)
      // Yalnız ekrandaki sekme: arka plandakini visibilitychange yönetiyor
      // (ekranda başka sekme varken geçmiyor).
      if (!document.hidden && !window.__adissooCartBusy && Date.now() - lastInput > QUIET_MS) {
        window.clearInterval(timer)
        applyUpdate()
      }
    }, 5000)
  },
})

/** Bekleyen yeni sürüm var mı (onNeedRefresh). */
let updateReady = false
/** Son dokunuş ya da tuş; sessiz anı ölçmek için. */
let lastInput = Date.now()
/** Bu kadar dokunulmazsa sessiz an. */
const QUIET_MS = 60_000

for (const ev of ['pointerdown', 'keydown']) {
  window.addEventListener(ev, () => (lastInput = Date.now()), { capture: true, passive: true })
}

/** Yeni sürüme geç: bekleyen çalışan devralınca sayfa bir kez yenilenir. */
function applyUpdate(): void {
  if (!updateReady) return
  updateReady = false
  void updateSW(true)
}

/**
 * Ekranda açık başka Adissoo sekmesi var mı. Yeni sürüme geçmek bütün
 * sekmeleri yeniliyor; arka plandaki sekme geçerse öndeki titrer.
 */
const tabs = 'BroadcastChannel' in window ? new BroadcastChannel('adissoo-sekmeler') : null
tabs?.addEventListener('message', (e) => {
  if (e.data === 'gorunen-var-mi' && !document.hidden) tabs.postMessage('gorunen-var')
})
function anotherTabVisible(): Promise<boolean> {
  if (!tabs) return Promise.resolve(false)
  return new Promise((resolve) => {
    const on = (e: MessageEvent) => {
      if (e.data !== 'gorunen-var') return
      tabs.removeEventListener('message', on)
      resolve(true)
    }
    tabs.addEventListener('message', on)
    tabs.postMessage('gorunen-var-mi')
    window.setTimeout(() => {
      tabs.removeEventListener('message', on)
      resolve(false)
    }, 300)
  })
}

// Arka plana alınınca (başka uygulamaya geçiş, ekran kilidi): kimse bakmıyor.
// Ekranda başka bir Adissoo sekmesi açıksa geçilmiyor, o titremesin.
// Birkaç saniye sonra soruluyor: yeni bir sekme açıldıysa o anda daha
// yükleniyor ve cevap veremiyor.
document.addEventListener('visibilitychange', () => {
  if (!document.hidden || !updateReady) return
  window.setTimeout(() => {
    if (!document.hidden || window.__adissooCartBusy) return
    void anotherTabVisible().then((busy) => {
      if (!busy && document.hidden) applyUpdate()
    })
  }, 5000)
})

/** Kontrolle başlayan indirme bitsin: yeni çalışan "bekliyor"a geçene kadar. */
function waitInstalled(reg: ServiceWorkerRegistration, timeoutMs: number): Promise<void> {
  const sw = reg.installing
  if (!sw) return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      window.clearTimeout(t)
      resolve()
    }
    const t = window.setTimeout(done, timeoutMs)
    sw.addEventListener('statechange', () => {
      if (sw.state !== 'installing') done()
    })
  })
}

/**
 * Reloads onto the newest build, for the pull-to-refresh gesture.
 *
 * A plain location.reload() is not enough here: the service worker answers from
 * its cache, so the reload would hand back the very build the user is trying to
 * get off. This asks the worker to check the server first, and lets it take
 * over if a new one is waiting, only then does the page reload.
 *
 * Falls back to a plain reload when there is no worker or the check fails, so
 * the gesture always does something.
 */
window.__adissooRefresh = async (minWaitMs = 0) => {
  // Sayfa bu süre dolmadan gitmiyor, güncelleme ne kadar hızlı bulunsa da.
  // Yenileyen kişi logonun çizilip dolduğunu görmeli; yarıda kesilen bir
  // çizim "bir şey ters gitti" gibi okunuyor. Kontrol bu arada koşuyor,
  // yani bekleme yavaş bağlantıda fazladan süre eklemiyor.
  const minWait = new Promise((r) => window.setTimeout(r, minWaitMs))
  try {
    const reg = await navigator.serviceWorker?.getRegistration()
    if (reg) {
      await reg.update()
      // İndirme sürüyorsa bitmesini bekle (20 Eki 2026). Eskiden beklemeden
      // "bekleyen yok" deyip yeniliyordu, indirme bitince sayfa bir daha
      // yenileniyordu: gelip tekrar kapanan sayfa buydu.
      await waitInstalled(reg, 15_000)
      // A worker waiting means a new build is already downloaded; activating it
      // is what makes the reload serve the new files, in a single reload.
      if (reg.waiting) {
        updateReady = false
        await minWait
        await updateSW(true)
        return
      }
    }
  } catch (err) {
    console.error('[refresh]', err)
  }
  await minWait
  window.location.reload()
}

// Android'in "ana ekrana ekle" olayı açılışta bir kez geliyor; kaçmasın.
captureInstallPrompt()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
